import { useCallback, useEffect, useRef } from 'react';
import { drawImageFill } from './utils';

interface UseDrawBlurConfig {
  canvas: HTMLCanvasElement | null;
  video: HTMLVideoElement | null;
}

export default function useDrawBlur({ canvas, video }: UseDrawBlurConfig) {
  const timeoutId = useRef<number>();

  const draw = useCallback(() => {
    if (!canvas || !video) return;

    drawImageFill(canvas.getContext('2d'), video);
  }, [canvas, video]);

  const drawLoop = useCallback(() => {
    function drawInternal() {
      draw();

      if (video && !video.paused) {
        timeoutId.current = window.requestAnimationFrame(drawInternal);
      }
    }

    timeoutId.current = window.requestAnimationFrame(drawInternal);
  }, [draw, video]);

  useEffect(() => {
    const handleLoadedData = () => {
      if (video) {
        // HACK: drawing only works after seek, so when video first loads,
        // seek to current position so that drawing works
        // eslint-disable-next-line no-param-reassign, no-self-assign
        video.currentTime = video?.currentTime;
        draw();
      }
    };

    if (!video) {
      return undefined;
    }

    if (video.readyState >= 1) {
      draw();
    } else {
      video.addEventListener('loadeddata', handleLoadedData);
    }

    video.addEventListener('play', drawLoop);
    video.addEventListener('seeked', draw);

    return () => {
      if (video) {
        video.removeEventListener('laodeddata', handleLoadedData);
        video.removeEventListener('play', drawLoop);
        video.removeEventListener('seeked', drawLoop);
      }
    };
  }, [draw, drawLoop, video]);

  useEffect(
    () => () => {
      if (timeoutId.current) {
        window.cancelAnimationFrame(timeoutId.current);
      }
    },
    [],
  );

  return { draw };
}
